import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  CircleUserRound,
  UserPlus2,
  CheckIcon,
  UserRoundPlus,
  X,
} from "lucide-react";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { ShortcutHandler } from "@/components/providers/ShortcutProvider";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { UserAvatar } from "../UserAvatar";
import { cn } from "@/lib/utils";

export interface AssigneeDropdownProps {
  users?: any[];
  value: string | null;
  onChange?: (userId: string | null) => void;
  size?: number;
  className?: string;
  showInviteOption?: boolean;
  onInvite?: () => void;
  variant?: "text" | "icon" | "text-dark";
  issueId?: string;
  borderless?: boolean;
  spanClassName?: string;
}

const AssigneeDropdown: React.FC<AssigneeDropdownProps> = ({
  issueId,
  users = [],
  value,
  onChange,
  size = 28,
  className = "",
  showInviteOption = false,
  onInvite,
  variant = "icon",
  borderless = false,
  spanClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateIssueAssignee = useIssuesStore(
    (state) => state.updateIssueAssignee,
  );
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  // Focus input when dropdown opens and clear when it closes
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Filter users based on search query
  const filteredUsers = (users || []).filter((user) =>
    (user.name || user.email)
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const baseDropdownClassName =
    "focus:outline-hidden focus:ring-0 active:bg-transparent focus-visible:outline-hidden focus-visible:ring-0 data-[state=open]:bg-transparent data-[state=closed]:bg-transparent active:ring-0 active:outline-hidden rounded-full bg-transparent p-0 transition-none";
  const baseSpanClassName = borderless
    ? "inline-flex items-center justify-center rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 text-xs text-secondary-n-400 cursor-pointer"
    : "inline-flex items-center justify-center border rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 border text-xs text-secondary-n-400 cursor-pointer";

  const selectedUser = users?.find((u) => u.id === value) || null;

  const textDarkDropdownNode = (
    <button
      className={`${baseDropdownClassName} ${className}`}
      data-state="closed"
      aria-label={
        selectedUser
          ? `Assignee: ${selectedUser.name || selectedUser.email}`
          : "Assign user"
      }
    >
      {selectedUser ? (
        <span
          className={cn(
            `gap-1 ${
              borderless ? "w-fit" : "w-full"
            } h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 ${
              !borderless ? "border-border-primary" : ""
            }`,
            baseSpanClassName,
            spanClassName,
          )}
        >
          <UserAvatar
            userName={selectedUser.name || selectedUser.email}
            userId={selectedUser.id}
            className="h-4 w-4 rounded-sm text-[7px]"
          />
          <span className="text-xs font-medium text-text-primary whitespace-nowrap">
            {selectedUser.name || selectedUser.email}
          </span>
        </span>
      ) : (
        <span
          className={cn(
            `gap-1 ${
              borderless ? "w-fit" : "w-full"
            } h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 ${
              !borderless ? "border-secondary-n-900" : ""
            }`,
            baseSpanClassName,
            spanClassName,
          )}
        >
          <UserRoundPlus className="w-4 h-4" color="var(--secondary-n-600)" />
          <span className="text-xs font-medium text-secondary-n-400">
            Assign
          </span>
        </span>
      )}
    </button>
  );

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          useProjectModalStore.getState().addProjectModal("assignee-dropdown");
        } else {
          useProjectModalStore
            .getState()
            .removeProjectModal("assignee-dropdown");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        {variant === "text-dark" ? (
          textDarkDropdownNode
        ) : (
          <button
            className={`${baseDropdownClassName} ${className} ${
              variant === "text" ? "w-auto h-auto" : size
            }`}
            data-state="closed"
            aria-label={
              selectedUser
                ? `Assignee: ${selectedUser.name || selectedUser.email}`
                : "Assign user"
            }
          >
            {!selectedUser ? (
              <span
                className={cn(
                  `${
                    variant === "icon"
                      ? `h-5 w-5 p-0.5 hover:brightness-150 ${
                          !borderless ? "border-secondary-n-900" : ""
                        }`
                      : `w-full h-5 px-2 bg-secondary-n-900 hover:bg-secondary-n-800 ${
                          !borderless ? "border-secondary-n-900" : ""
                        }`
                  }`,
                  baseSpanClassName,
                  spanClassName,
                )}
              >
                {variant === "icon" ? (
                  <UserRoundPlus
                    className="w-full h-full cursor-pointer"
                    color="var(--secondary-n-400)"
                  />
                ) : (
                  <span
                    className={cn(
                      "flex items-center gap-1 pt-[3px] pb-[3px] cursor-pointer bg-transparent",
                      spanClassName,
                    )}
                  >
                    <UserRoundPlus
                      className="w-3 h-3"
                      color="var(--secondary-n-400)"
                    />
                    <span className="link/text-xs font-medium text-secondary-n-400">
                      Assign
                    </span>
                  </span>
                )}
              </span>
            ) : (
              <UserAvatar
                userName={selectedUser.name || selectedUser.email}
                userId={selectedUser.id}
              />
            )}
          </button>
        )}
      </DropdownMenuTrigger>
      {isOpen && (
        <ShortcutHandler enabled={true}>
          <DropdownMenuContent
            align="start"
            ref={menuRef}
            className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              searchInputRef.current?.focus();
            }}
            onPointerDownOutside={(e) => {
              if (
                !menuRef.current?.contains(e.target as Node) &&
                !searchInputRef.current?.contains(e.target as Node)
              ) {
                setIsOpen(false);
              }
            }}
            onInteractOutside={(e) => {
              if (searchInputRef.current?.contains(e.target as Node)) {
                e.preventDefault();
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-1">
              <div className="relative">
                <Input
                  ref={searchInputRef}
                  placeholder="Assign to..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-7 text-sm bg-secondary-n-950 border-0! border-none! focus:border-0! focus:border-none! focus-visible:border-0! focus-visible:border-none! focus-visible:ring-0! focus-visible:!ring-none placeholder:text-secondary-n-600 pr-6"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    if (menuRef.current?.contains(e.relatedTarget as Node)) {
                      e.preventDefault();
                      searchInputRef.current?.focus();
                    }
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            </div>
            <DropdownMenuSeparator />

            {/* No Assignee Option */}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (issueId) {
                  updateIssueAssignee(supabaseClient, issueId, null);
                } else {
                  onChange?.(null);
                }
              }}
              className="flex items-center w-full py-1 pl-2"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center">
                  <CircleUserRound className="h-5 w-5 text-secondary-n-600" />
                </div>
                <span>No assignee</span>
              </div>
              {value === null && (
                <CheckIcon className="ml-auto size-3.5" color="#F5F5F6" />
              )}
            </DropdownMenuItem>

            {/* Current Assignee Section */}
            {selectedUser && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  key={selectedUser.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (issueId) {
                      updateIssueAssignee(
                        supabaseClient,
                        issueId,
                        selectedUser,
                      );
                    } else {
                      onChange?.(selectedUser.id);
                    }
                  }}
                  className="flex items-center w-full py-1 pl-2"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <UserAvatar
                        userName={selectedUser.name || selectedUser.email}
                        userId={selectedUser.id}
                      />
                    </div>
                    <span className="truncate max-w-[120px]">
                      {selectedUser.name || selectedUser.email}
                    </span>
                  </div>
                  <CheckIcon className="ml-auto size-3.5" color="#F5F5F6" />
                </DropdownMenuItem>
              </>
            )}

            {/* Team Members Section */}
            {filteredUsers.filter((user) => user.id !== value).length > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-xs font-medium text-secondary-n-400">
                  Team members
                </div>
                <div
                  className="overflow-y-auto overflow-x-hidden context-menu-scroll max-h-[calc(70vh-320px)]"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                  }}
                >
                  {filteredUsers
                    .filter((user) => user.id !== value)
                    .map((user) => (
                      <DropdownMenuItem
                        key={user.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (issueId) {
                            updateIssueAssignee(supabaseClient, issueId, user);
                          } else {
                            onChange?.(user.id);
                          }
                        }}
                        className="flex items-center w-full py-1 pl-2"
                      >
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            userName={user.name || user.email}
                            userId={user.id}
                          />
                          <span className="truncate max-w-[120px]">
                            {user.name || user.email}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                </div>
              </>
            )}

            {showInviteOption && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onInvite}
                  className="flex items-center gap-2 text-sm cursor-pointer rounded-md text-primary"
                >
                  <UserPlus2 className="h-5 w-5" />
                  <span>Invite and assign...</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </ShortcutHandler>
      )}
    </DropdownMenu>
  );
};

export default AssigneeDropdown;
